import { FlexBox, Spinner, Text } from '@dixa/lib-shared-habla';
import { PropsWithChildren } from 'lib-shared-types-utils';
import { useEffect } from 'react';
import { useUpdateEffect } from 'react-use';
import { Waypoint } from 'react-waypoint';

import { searchApi } from 'apis/search';
import { usePaginatedEndpoint } from 'utils/apis/pagination/usePaginatedEndpoint';
import { pubSub } from 'utils/pubSub';
import { useAppPathParams } from 'utils/url/useAppPathParams';
import { useAppSearchParams } from 'utils/url/useAppSearchParams';

import { ContactsCompany } from './components/company/contactsCompany';

const ContactListMeta = ({ children }: PropsWithChildren) => {
  return (
    <FlexBox justifyContent="center" alignItems="center" style={{ height: 72 }}>
      {children}
    </FlexBox>
  );
};

export const ContactsCompanies = (): JSX.Element => {
  const { id } = useAppPathParams('/contacts/company/:id');

  const {
    params: { q: searchTerm = '' },
  } = useAppSearchParams('/contacts/company/:id');

  const {
    items: contacts,
    asyncStatus,
    nextPage,
    refetchItems,
    search,
    hasNextPage,
  } = usePaginatedEndpoint({
    endpoint: searchApi.endpoints.readSearchCompany,
    itemsSelector: ({ data }) => data.results.hits,
    infinite: true,
    initialArg: {
      sort: { key: 'name', direction: 'asc' },
      limit: 50,
      offset: 0,
    },
  });

  useUpdateEffect(() => {
    search(searchTerm);
  }, [searchTerm, search]);

  useEffect(() => {
    const unsubscribe = pubSub.subscribe('companyModalCompanyAdded', () => {
      // Delaying refetch so posgress has time
      // to provision the dynamoDB database
      setTimeout(() => {
        refetchItems();
      }, 2000);
    });

    return () => unsubscribe();
  }, [refetchItems]);

  if (asyncStatus === 'rejected') {
    return (
      <ContactListMeta>
        <Text.Paragraph>
          An error occurred while getting users list
        </Text.Paragraph>
      </ContactListMeta>
    );
  }

  if (asyncStatus === 'pending' && !contacts.length) {
    return (
      <ContactListMeta>
        <Spinner />
      </ContactListMeta>
    );
  }

  return (
    <>
      {contacts.map((company) => (
        <ContactsCompany
          key={company.id}
          company={company}
          selected={company.id === id}
        />
      ))}

      {asyncStatus === 'pending' && (
        <ContactListMeta>
          <Spinner />
        </ContactListMeta>
      )}

      {asyncStatus === 'fulfilled' && hasNextPage && (
        <Waypoint onEnter={nextPage} />
      )}
    </>
  );
};
